import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ActionCell, FieldCell } from './TableCells';
import { useTranslation } from '../../../../../custom-hooks/useTranslation';
import { EXPRESSION_EDITOR } from '../../../../constants/editor.constants';
import TablePropertiesModal from '../TablePropertiesModal';

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableBody-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="EmptyRow-middleAlign"]': {
      height: '90px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
    '& [class*="MuiFormControl-marginNormal"]': {
      marginTop: '0px',
    },
  },
});

const columns = [
  {
    header: 'Prompt Message',
    accessor: 'promptMessageToDisplay',
    customCell: FieldCell,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const ExtraPromptsModal = ({ controlId, name, currentControlModel, setCurrentControlModel }) => {
  const currentPropertyValue = currentControlModel[name];

  const classes = useStyles();
  const translateString = useTranslation();

  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [rows, setRows] = useState(currentPropertyValue ?? []);

  const currentKey = useRef('');

  useEffect(() => {
    setRows(
      currentPropertyValue?.length
        ? currentPropertyValue.map(({ id, promptMessage = '', type = 'formula' }) => {
            const promptMessageToDisplay = promptMessage.startsWith('eval||')
              ? promptMessage.split('eval||')[1]
              : promptMessage;

            return { id, promptMessage, type, promptMessageToDisplay };
          })
        : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPropertyValue]);

  const updateLocalState = (newRowsTemp) => {
    const newRows = newRowsTemp || rows;

    if (controlId && name) {
      const controlModel = {
        ...currentControlModel,
        [name]: newRows.length
          ? newRows.map(({ id, promptMessageToDisplay = '', type }) => {
              const promptMessage =
                promptMessageToDisplay && type === 'formula'
                  ? `eval||${promptMessageToDisplay}`
                  : promptMessageToDisplay;

              return { id, promptMessage, type };
            })
          : undefined,
      };
      setCurrentControlModel(controlModel);
      setRows(newRows);
    }
  };

  const addRow = () => {
    const rowId = `${uuidv4()}`;
    const newRow = {
      id: rowId,
      promptMessage: '',
      promptMessageToDisplay: '',
      type: 'formula',
    };

    updateLocalState([...rows, newRow]);
  };

  const deleteRow = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    updateLocalState(newRows);
  };

  const editRow = (id, key, value = '') => {
    const updatedRows = rows.map((row) => (row.id === id ? { ...row, [key]: value } : row));
    updateLocalState(updatedRows);
  };

  const handleOpenTablePropertiesModal = (key, row) => {
    currentKey.current = key;
    setIsTablePropertiesModalOpen(true);
    setSelectedRow({ ...row, currentKey: key });
  };

  const CustomHeader = () => {
    const buttonText = translateString('Add New Prompt');
    return (
      <div>
        <Button variant="text" size="small" onClick={addRow}>
          {buttonText}
        </Button>
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <Table
        title={translateString('Extra Prompts')}
        columns={columns}
        initialSortOrder="asc"
        hidePagination
        rows={rows.map((row) => ({
          ...row,
          rows,
          key: row.id,
          editRow,
          deleteRow,
          setSelectedRow,
          handleOpenTablePropertiesModal,
        }))}
        CustomHeader={CustomHeader}
      />
      {isTablePropertiesModalOpen && (
        <TablePropertiesModal
          type={EXPRESSION_EDITOR}
          row={selectedRow}
          handleUpdate={(id, key, value) => editRow(id, key, value)}
          onClose={() => setIsTablePropertiesModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ExtraPromptsModal;
